// STRUCTURE-CHANGED (docs/design/2026-10-30-shared-des-and-fulfillment.md §3.2; founder-approved
// 2026-10-30): a reporting-line change as its leaders see it — who moved, from and to, the agents
// told, and the work addressed to the old leader listed for re-routing — and the one-click
// re-route: each item taken over by the new leader (a task is handed off with a comment, an
// approval or status ask re-pointed) or left with the old one. The change itself is written by
// the `org.structure` consumer from the write's own event; nothing here moves a line.
import { z } from "zod";
import { StructureChangeKind, StructureSubjectKind } from "../enums";
import { Timestamp, Uuid } from "../primitives";
import { CompanyItemParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

const Named = z.object({ id: Uuid, name: z.string() });

export const StructureRerouteKind = z
  .enum(["task", "approval", "status_ask"])
  .meta({ id: "StructureRerouteKind" });
export const StructureRerouteState = z
  .enum(["open", "taken", "left"])
  .meta({ id: "StructureRerouteState" });

export const StructureRerouteItemView = z
  .object({
    kind: StructureRerouteKind,
    id: Uuid,
    title: z.string(),
    state: StructureRerouteState,
    decided_by_membership_id: Uuid.nullable(),
    decided_at: Timestamp.nullable(),
  })
  .meta({ id: "StructureRerouteItem" });

export const StructureChangeView = z
  .object({
    id: Uuid,
    kind: StructureChangeKind,
    subject_kind: StructureSubjectKind,
    subject_id: Uuid,
    /** The seat's title or the AG's name. */
    subject_name: z.string(),
    /** One line, as the reorg planner says it ("re-point seat AM 2: CEO → Fulfillment Lead"). */
    summary: z.string(),
    from: Named.nullable(),
    to: Named.nullable(),
    old_leader: Named.nullable(),
    new_leader: Named.nullable(),
    /** The DEs and AGs told ("From now on you report to …"). */
    agents: z.array(
      z.object({
        kind: z.enum(["digital_employee", "workforce_agent"]),
        id: Uuid,
        name: z.string(),
      }),
    ),
    /** The note each agent got (the same words for all of them). */
    note: z.string().nullable(),
    reroute: z.array(StructureRerouteItemView),
    created_at: Timestamp,
    /** The caller may take items over or leave them (the new leader, or a manager). */
    can_reroute: z.boolean(),
  })
  .meta({ id: "StructureChangeView" });
export type StructureChangeView = z.infer<typeof StructureChangeView>;

export const StructureReroute = z
  .strictObject({
    items: z
      .array(
        z.strictObject({
          kind: StructureRerouteKind,
          id: Uuid,
          action: z.enum(["take_over", "leave"]),
        }),
      )
      .min(1)
      // A change lists up to 100 tasks, 100 approvals and 100 status asks ("Take over all").
      .max(300),
  })
  .meta({ id: "StructureReroute" });
export type StructureReroute = z.infer<typeof StructureReroute>;

export const structureRoutes = {
  "structureChanges.get": defineRoute({
    ...core,
    id: "structureChanges.get",
    method: "get",
    path: `${base}/structure-changes/{id}`,
    tag: "org",
    summary: "A reporting-line change: who moved, the agents told, the items to re-route",
    description:
      "Managers and the change's old and new leader; anyone else 404. Written by the `org.structure` consumer after the move (chart, table, seat drawer, agent page, API, MCP or the reorg script).",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: StructureChangeView },
    errors: [],
    emits: [],
  }),
  "structureChanges.reroute": defineRoute({
    ...core,
    id: "structureChanges.reroute",
    method: "post",
    path: `${base}/structure-changes/{id}/reroute`,
    tag: "org",
    summary: "Take listed items over (to the new leader) or leave them with the old one",
    description:
      "The new leader or a manager. Take over: a task is handed off from the old leader's seat to the new leader's with the reason `re-routed after structure change`; a pending approval's approver seat and an open status ask's target move to the new leader. An item already decided, or no longer open (done, decided, answered), is left as it is and not repeated.",
    access: "member",
    params: CompanyItemParams,
    body: StructureReroute,
    idempotency: "optional",
    response: { status: 200, schema: StructureChangeView },
    errors: [],
    emits: ["structure.rerouted", "task.assigned", "task.unassigned", "task.handed_off"],
  }),
} as const;
